import { useState } from 'react'
import { colors, text, space, radius } from '../design/tokens'

/**
 * Paginador único de LGR.
 *
 * Cada pantalla tenía el suyo hecho a mano, y todos hacían lo mismo: Anterior,
 * "Página X de Y", Siguiente. Con eso, llegar a la página 9 son ocho clics y
 * no hay forma de ir a la última — que es donde suele estar lo que se busca
 * cuando el listado viene ordenado por fecha.
 *
 * Este suma las dos cosas que faltaban: **saltar a una página escribiéndola** y
 * **ir a la última de un clic**. Los ⏮ ⏭ aparecen solo cuando hay más de dos
 * páginas: con dos, Anterior y Siguiente ya alcanzan y agregar botones es ruido.
 *
 * Uso:
 *   <Paginador pagina={p} totalPaginas={n} onCambiar={setP} info="120 remitos" />
 */
export default function Paginador({ pagina, totalPaginas, onCambiar, info = null }) {
  const total = Math.max(1, Number(totalPaginas) || 1)
  const actual = Math.min(Math.max(1, Number(pagina) || 1), total)
  const [escrito, setEscrito] = useState(String(actual))
  const [ultima, setUltima] = useState(actual)

  // Si la página cambia por afuera (los botones, un filtro, una recarga), el
  // input la sigue. Se ajusta durante el render y no con un efecto: así no hay
  // un fotograma con el número viejo.
  if (ultima !== actual) {
    setUltima(actual)
    setEscrito(String(actual))
  }

  if (total <= 1 && !info) return null

  const ir = (n) => {
    const destino = Math.min(Math.max(1, Number(n) || 1), total)
    if (destino !== actual) onCambiar(destino)
  }

  const saltar = () => {
    const n = parseInt(escrito, 10)
    if (!Number.isNaN(n)) ir(n)
    else setEscrito(String(actual))
  }

  const btn = (contenido, destino, deshabilitado, titulo) => (
    <button
      type="button"
      title={titulo}
      style={{ ...st.btn, ...(deshabilitado ? st.btnOff : {}) }}
      disabled={deshabilitado}
      onClick={() => ir(destino)}
    >{contenido}</button>
  )

  const muchas = total > 2

  return (
    <div style={st.wrap}>
      {info && <span style={st.info}>{info}</span>}
      <div style={st.btns}>
        {muchas && btn('«', 1, actual <= 1, 'Primera página')}
        {btn('‹ Anterior', actual - 1, actual <= 1)}

        <span style={st.pos}>
          Página
          <input
            style={st.input}
            value={escrito}
            onChange={e => setEscrito(e.target.value.replace(/\D/g, ''))}
            onBlur={saltar}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); saltar() } }}
            aria-label="Ir a la página"
            inputMode="numeric"
          />
          de {total}
        </span>

        {btn('Siguiente ›', actual + 1, actual >= total)}
        {muchas && btn('»', total, actual >= total, 'Última página')}
      </div>
    </div>
  )
}

const st = {
  wrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: space.lg, flexWrap: 'wrap', gap: space.md },
  info: { fontSize: text.sec, color: colors.textSec },
  btns: { display: 'flex', alignItems: 'center', gap: space.sm, marginLeft: 'auto' },
  pos: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: text.sec, color: '#475569', fontWeight: 600, padding: `0 ${space.sm}px` },
  input: {
    width: 52, padding: '5px 6px', textAlign: 'center',
    border: `1px solid ${colors.border}`, borderRadius: radius.sm,
    fontSize: text.sec, fontWeight: 600, color: colors.text,
    fontVariantNumeric: 'tabular-nums',
  },
  btn: { padding: '6px 12px', background: colors.bg, border: `1px solid ${colors.textPlh}`, borderRadius: radius.sm, cursor: 'pointer', fontSize: text.body, color: colors.text, fontWeight: 500 },
  btnOff: { color: colors.textPlh, cursor: 'not-allowed', background: colors.bgSoft },
}
